import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CircleNotch, ArrowLeft } from "@phosphor-icons/react";

const LOGO = "/novuspak-logo.png";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("La contraseña debe tener al menos 6 caracteres.");
      return;
    }
    if (password !== confirm) {
      setError("Las contraseñas no coinciden.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Contraseña actualizada. Ya puedes iniciar sesión.");
      navigate("/login");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12 bg-[#F8F9FA]">
      <div className="w-full max-w-md animate-fade-up" data-testid="reset-password-page">
        <div className="flex items-center gap-3 mb-8">
          <img src={LOGO} alt="NovusPak" className="h-12 w-12 object-contain shrink-0" />
          <span className="font-heading font-extrabold text-xl text-[#0F172A] leading-tight">Control de Inventario</span>
        </div>

        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight text-[#0F172A]">Nueva contraseña</h1>
        <p className="text-[#475569] mt-2 mb-8">Crea una nueva contraseña para tu cuenta.</p>

        {!token ? (
          <p data-testid="reset-no-token" className="text-sm text-[#991B1B] bg-[#FEF2F2] border border-[#FECACA] rounded-md px-3 py-2">
            Enlace inválido o incompleto. Solicita un nuevo enlace desde "¿Olvidaste tu contraseña?".
          </p>
        ) : (
          <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-[0.15em] text-[#64748B]">Nueva contraseña</Label>
              <Input
                id="password"
                data-testid="reset-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-11 bg-white border-[#E2E8F0] focus-visible:ring-[#0F172A]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm" className="text-xs font-semibold uppercase tracking-[0.15em] text-[#64748B]">Repite la contraseña</Label>
              <Input
                id="confirm"
                data-testid="reset-confirm-input"
                type="password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="••••••••"
                className="h-11 bg-white border-[#E2E8F0] focus-visible:ring-[#0F172A]"
              />
            </div>

            {error && (
              <p data-testid="reset-error" className="text-sm text-[#991B1B] bg-[#FEF2F2] border border-[#FECACA] rounded-md px-3 py-2">{error}</p>
            )}

            <Button type="submit" data-testid="reset-submit" disabled={loading} className="w-full h-11 bg-[#0F172A] hover:bg-[#1E293B] text-white font-semibold">
              {loading ? <CircleNotch size={18} className="animate-spin" /> : "Guardar contraseña"}
            </Button>
          </form>
        )}

        <Link to="/login" data-testid="back-to-login" className="inline-flex items-center gap-1.5 mt-6 text-sm font-semibold text-[#475569] hover:text-[#0F172A]">
          <ArrowLeft size={16} weight="bold" /> Volver a iniciar sesión
        </Link>
      </div>
    </div>
  );
}
